import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useLocation } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Star, Users, Globe, Award, Zap, Shield } from 'lucide-react';

const CATEGORIES = {
  'modular-stands': {
    slug: 'modular-stands',
    title: 'Modular Exhibition Stands | ExpoStand',
    h1: 'Modular Exhibition Stands',
    metaDescription: 'Professional modular exhibition stands for trade shows worldwide. Reusable, cost-effective, and quick to assemble. Get instant quotes from verified contractors.',
    keywords: 'modular exhibition stands, modular trade show stands, modular display stands, modular stand design',
    description: 'Modular exhibition stands offer the perfect balance of flexibility, quality, and cost-effectiveness. Ideal for companies that participate in multiple trade shows annually.',
    benefits: [
      'Reusable for multiple events — maximize your investment',
      'Quick assembly and dismantling — save on labour costs',
      'Easily reconfigurable to fit different booth sizes',
      'High-quality materials with professional finish',
      'Lightweight and easy to transport worldwide',
    ],
    features: [
      { title: 'System Build', desc: 'Lightweight aluminium frames with interchangeable panels' },
      { title: 'Custom Graphics', desc: 'Full print graphics on fabric or rigid panels' },
      { title: 'Storage Solutions', desc: 'Built-in counters, shelving, and storage cabinets' },
      { title: 'AV Integration', desc: 'Monitor mounts, LED lighting, and digital display support' },
    ],
    priceRange: '€8,000 – €45,000',
    idealFor: ['Companies exhibiting 3+ times/year', 'International trade show participants', 'Brands needing consistent look & feel'],
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
  },
  'custom-stands': {
    slug: 'custom-stands',
    title: 'Custom Exhibition Stands | ExpoStand',
    h1: 'Custom Exhibition Stands',
    metaDescription: 'Bespoke custom exhibition stands designed and built to your exact specifications. Award-winning designs for major international trade shows. Request a quote today.',
    keywords: 'custom exhibition stands, bespoke trade show stands, custom stand design, exhibition stand builder',
    description: 'Custom exhibition stands are fully bespoke solutions designed to reflect your brand identity. Perfect for flagship events and major trade shows where first impressions matter.',
    benefits: [
      'Completely unique design tailored to your brand',
      'Maximum visual impact at major trade shows',
      'Premium materials and craftsmanship',
      'Full project management from design to build',
      'Integrated technology and interactive experiences',
    ],
    features: [
      { title: 'Bespoke Architecture', desc: 'Unique structural design by award-winning architects' },
      { title: 'Premium Materials', desc: 'High-end wood, metal, glass, and fabric combinations' },
      { title: 'Brand Immersion', desc: 'Every element reflects your brand guidelines' },
      { title: 'Interactive Tech', desc: 'VR, AR, touch screens, and digital activations' },
    ],
    priceRange: '€25,000 – €250,000+',
    idealFor: ['Major brand activations', 'Flagship trade show presentations', 'Companies seeking competitive advantage'],
    image: 'https://images.unsplash.com/photo-1559223607-a43c990c692c?w=1200&q=80',
  },
  'double-decker-stands': {
    slug: 'double-decker-stands',
    title: 'Double Decker Exhibition Stands | ExpoStand',
    h1: 'Double Decker Exhibition Stands',
    metaDescription: 'Maximize your exhibition floor space with double decker stands. Private meeting rooms, VIP areas, and panoramic views. Built to international safety standards.',
    keywords: 'double decker exhibition stands, two storey stands, multi-level exhibition stands, double deck stands',
    description: 'Double decker exhibition stands dramatically increase your usable space without increasing floor footprint. Perfect for large-scale trade shows where private meetings and VIP spaces are essential.',
    benefits: [
      'Double your exhibition space on the same floor area',
      'Private meeting rooms away from the show floor noise',
      'VIP hospitality areas with panoramic event views',
      'Strong visual presence that dominates the hall',
      'Built to strict international safety and fire standards',
    ],
    features: [
      { title: 'Structural Engineering', desc: 'Certified steel or aluminium structural frames' },
      { title: 'Private Upper Floor', desc: 'Meeting rooms, hospitality, and storage' },
      { title: 'Open Ground Floor', desc: 'Product display, reception, and engagement areas' },
      { title: 'Safety Compliance', desc: 'All local safety codes met including load testing' },
    ],
    priceRange: '€50,000 – €300,000+',
    idealFor: ['Large corporate exhibitors', 'Companies needing private meeting space', 'Venues allowing upper structures'],
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1200&q=80',
  },
};

const STATS = [
  { value: '500+', label: 'Verified Contractors' },
  { value: '50+', label: 'Countries Covered' },
  { value: '10,000+', label: 'Stands Built' },
  { value: '4.8/5', label: 'Average Rating' },
];

export default function CategoryLanding() {
  const { categorySlug: paramSlug } = useParams();
  const location = useLocation();
  // For static routes like /modular-stands (no :categorySlug param), extract from pathname
  const categorySlug = paramSlug || location.pathname.replace(/^\//, '').split('/').pop();
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', company: '', event: '', budget: '' });
  const [submitted, setSubmitted] = useState(false);

  const category = CATEGORIES[categorySlug];
  const canonicalBase = 'https://expostand.com';

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Category Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{category.title}</title>
        <meta name="description" content={category.metaDescription} />
        <meta name="keywords" content={category.keywords} />
        <link rel="canonical" href={`${canonicalBase}/${category.slug}`} />
        <link rel="alternate" hrefLang="en" href={`${canonicalBase}/${category.slug}`} />
        <link rel="alternate" hrefLang="tr" href={`${canonicalBase}/tr/${category.slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${canonicalBase}/${category.slug}`} />
        <meta property="og:title" content={category.title} />
        <meta property="og:description" content={category.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${canonicalBase}/${category.slug}`} />
        <meta property="og:image" content={category.image} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": category.h1,
          "description": category.description,
          "provider": { "@type": "Organization", "name": "ExpoStand" },
          "areaServed": "Worldwide",
          "url": `${canonicalBase}/${category.slug}`,
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-10 h-8" />
            <div className="text-slate-700 font-semibold leading-tight"><div>EXPO</div><div>STAND</div></div>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <Link to="/modular-stands" className="hover:text-blue-600">Modular Stands</Link>
            <Link to="/custom-stands" className="hover:text-blue-600">Custom Stands</Link>
            <Link to="/contractor/login" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Get a Quote</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#142549] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={category.image} alt={category.h1} className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-medium px-3 py-1.5 rounded-full mb-5">
              <Star className="h-3.5 w-3.5" />Exhibition Stands
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-5 leading-tight">{category.h1}</h1>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">{category.description}</p>
            <div className="flex flex-wrap gap-3">
              <a href="#get-quote"
                className="inline-flex items-center gap-2 bg-[#FFD54A] text-[#142549] font-bold px-7 py-3.5 rounded-xl hover:bg-yellow-400 transition-colors"
              >
                Get Free Quotes <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-xl hover:bg-white/10 transition-colors"
              >
                View All Categories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map((s, i) => (
            <div key={i}>
              <div className="text-3xl font-extrabold">{s.value}</div>
              <div className="text-blue-200 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 max-w-5xl mx-auto px-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Why Choose {category.h1}?</h2>
        <p className="text-gray-500 mb-8">Key benefits that make this option ideal for your next exhibition.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {category.benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-green-50 border border-green-100 rounded-xl p-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <span className="text-gray-700">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">What's Included</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {category.features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                  <Zap className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{f.title}</h3>
                <p className="text-sm text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For + Price */}
      <section className="py-16 max-w-5xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Ideal For</h2>
            <ul className="space-y-3">
              {category.idealFor.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-gray-700">
                  <Award className="h-4 w-4 text-amber-500 shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#142549] text-white rounded-2xl p-8">
            <p className="text-blue-200 text-sm mb-2">Typical Investment Range</p>
            <p className="text-3xl font-extrabold mb-4">{category.priceRange}</p>
            <p className="text-blue-200 text-sm mb-5">Exact pricing depends on size, design complexity, and location. Get free quotes from multiple verified contractors.</p>
            <a href="#get-quote" className="inline-flex items-center gap-2 bg-[#FFD54A] text-[#142549] font-bold px-5 py-2.5 rounded-xl hover:bg-yellow-400 transition-colors text-sm">
              Get My Quote <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="get-quote" className="bg-[#F5F7FA] py-16">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Get Free Quotes</h2>
            <p className="text-gray-500 text-sm mb-6">Tell us about your project and receive quotes from up to 5 verified contractors within 48 hours.</p>
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Request Received!</h3>
                <p className="text-gray-500 text-sm">We'll match you with verified contractors and send quotes within 48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Full Name *</label>
                    <input required value={quoteForm.name} onChange={e => setQuoteForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Smith" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Email *</label>
                    <input required type="email" value={quoteForm.email} onChange={e => setQuoteForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="john@company.com" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Company *</label>
                  <input required value={quoteForm.company} onChange={e => setQuoteForm(p => ({ ...p, company: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Company Name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Exhibition / Event Name *</label>
                  <input required value={quoteForm.event} onChange={e => setQuoteForm(p => ({ ...p, event: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Hannover Messe 2025" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Approximate Budget (EUR)</label>
                  <select value={quoteForm.budget} onChange={e => setQuoteForm(p => ({ ...p, budget: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                    <option value="">Select budget range</option>
                    <option>€5,000 – €15,000</option>
                    <option>€15,000 – €30,000</option>
                    <option>€30,000 – €60,000</option>
                    <option>€60,000 – €100,000</option>
                    <option>€100,000+</option>
                  </select>
                </div>
                <button type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                  Request Free Quotes <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-gray-400 text-center flex items-center justify-center gap-1">
                  <Shield className="h-3 w-3" /> Your information is secure. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#142549] text-blue-200 py-10 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-8 h-6 opacity-80" />
            <span className="text-white font-semibold">EXPO STAND</span>
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/modular-stands" className="hover:text-white">Modular Stands</Link>
            <Link to="/custom-stands" className="hover:text-white">Custom Stands</Link>
            <Link to="/double-decker-stands" className="hover:text-white">Double Decker</Link>
          </div>
          <p className="text-xs">© 2025 ExpoStand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
